<template>

    <div class="open-layers-map">

        <div ref="map" class="map"></div>

        <div ref="mapTooltip" class="map-tooltip"></div>

        <div class="control-group zoom-control">
            <cms-button @click="onZoomClick(1)" class="zoom-in" title="Zoom in">
                <i></i>
            </cms-button>
            <div class="divider"></div>
            <cms-button @click="onZoomClick(-1)" class="zoom-out" title="Zoom uit">
                <i></i>
            </cms-button>
        </div>

    </div>

</template>

<script>

    import Component from 'vue-class-component'
    import Vue from 'vue'
    import di from 'di'

    import find from 'lodash/find'
    import debounce from 'lodash/debounce'

    import Map from 'ol/Map'
    import View from 'ol/View'
    import { createEmpty, extend } from 'ol/extent'

    import Feature from 'ol/Feature'
    import SrcVector from 'ol/source/Vector'
    import Cluster from 'ol/source/Cluster'
    import LayerVector from 'ol/layer/Vector'
    import Point from 'ol/geom/Point'
    import Overlay from 'ol/Overlay'
    import GeoJSON from 'ol/format/GeoJSON'

    import DrawInteraction from 'ol/interaction/Draw';

    import getWmtsRdLayer from "app/utils/getWmtsRdLayer";
    import getMarkerStyle from "app/styles/getMarkerStyle";
    import getClusterStyle from "app/styles/getClusterStyle";
    import getReferenceStyle from "app/styles/getReferenceStyle";

    import getDrawingLayer from "app/utils/getDrawingLayer";

    @Component({

        name: 'open-layers-map',
        props: {
            markers: {
                type: Array,
                default: () => []
            },
            clustered: {
                type: Boolean,
                default: false
            },
            selectable: {
                type: Boolean,
                default: false
            }
        }
    })
    export default class OpenLayersMap extends Vue {

        data(){

            return {
                tileLoading: 0,
                tileLoaded: 0,
                allCoordinates: []
            }
        }

        created() {

            this.logger = this.$logger.child('OpenLayersMap');

            this.debouncedTileLoadingCompleted = debounce(this.tileLoadingCompleted.bind(this), 1000);
        }

        mounted() {

            this.$watch('markers', () => {

                this.clearMarkers();
                this.addMarkers();
            });

            this.$watch('selectable', (selectable) => {

                if(selectable){
                    this.addSelectInteraction()
                }
                else {
                    this.removeSelectInteraction()
                }
            })

            this.setupMap();

            if(this.selectable){
                this.addSelectInteraction()
            }
        }

        setupMap() {

            // Drawing
            const drawingLayer = getDrawingLayer();
            this.drawSource = drawingLayer.getSource()

            // Base layers
            this.backgroundTileLayer = getWmtsRdLayer('https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0?request=getcapabilities&service=wmts', "grijs");
            this.backgroundTileLayer.getSource().on('tileloadstart', this.tileLoadingStarted);
            this.backgroundTileLayer.getSource().on('tileloadend', this.tileLoadingEnded);
            this.backgroundTileLayer.getSource().on('tileloaderror', this.tileLoadingEnded);

            this.kadestraleTileLayer = getWmtsRdLayer('https://service.pdok.nl/lv/bgt/wmts/v1_0?request=GetCapabilities&service=WMTS', "omtrekgerichtevisualisatie");
            this.kadestraleTileLayer.getSource().on('tileloadstart', this.tileLoadingStarted);
            this.kadestraleTileLayer.getSource().on('tileloadend', this.tileLoadingEnded);
            this.kadestraleTileLayer.getSource().on('tileloaderror', this.tileLoadingEnded);

            // Tooltip overlay
            this.overlay = new Overlay({
                element: this.$refs.mapTooltip,
                autoPan: true,
                autoPanAnimation: {
                    duration: 250
                }
            });

            this.map = new Map({

                layers: [
                    this.backgroundTileLayer,
                    this.kadestraleTileLayer,
                    drawingLayer
                ],
                target: this.$refs.map,
                controls: [],
                overlays: [
                    this.overlay
                ],
                view: new View({
                    // minZoom: 3,
                    projection: 'EPSG:28992',
                    center: [135910, 456120],
                    zoom: 3
                })
            });

            this.setupReferenceOverlay();
            this.setupMarkerOptions();

            this.map.on('click', this.onClick, this);
        }

        destroyed() {

            this.map.un('click', this.onClick, this);
        }

        closeTooltip() {

            this.$emit('close-tooltip');
            this.overlay.setPosition(undefined);
        }

        showDetails() {

            this.$emit('detail-click');
        }

        onClick(event) {

            this.$emit('clicked', event);

            let features = this.map.getFeaturesAtPixel(event.pixel);

            if(!features || !features.length) {
                this.closeTooltip();
                return;
            }

            const markers = features[0].get('features');
            if(!markers){
                return;
            }

            if(markers.length > 1) {

                let extent = null;
                let emptyExtent = createEmpty();

                for(let marker of markers) {
                    if(!extent) {
                        extent = extend(emptyExtent, marker.getGeometry().getExtent());
                    }
                    else {
                        extent = extend(extent, marker.getGeometry().getExtent());
                    }
                }

                this.map.getView().fit(extent, {
                    padding: [50, 50, 50, 50]
                });

                return;
            }

            this.$emit('marker-click', markers[0].getId());
        }

        setupMarkerOptions() {

            this.markersSource = new SrcVector({
                features: []
            });

            this.markersSource.set('identifier', 'markers', true);

            const clusterSource = new Cluster({
                id: 'cluster',
                distance: 15,
                source: this.markersSource
            });

            const self = this;

            const vectorLayer = new LayerVector({
                id: 'vector',
                source: this.clustered ? clusterSource : this.markersSource,
                style: function(feature) {

                    const features = feature.get('features');

                    if(!features) {

                        const marker = find(self.markers, { id: feature.getId() });
                        return getMarkerStyle(marker);
                    }

                    const size = features.length;

                    if(size === 1) {

                        const currentFeature = feature.get('features')[0];
                        const marker = find(self.markers, { id: currentFeature.getId() });

                        return getMarkerStyle(marker);
                    }
                    else {

                        return getClusterStyle(size);
                    }
                }
            });

            this.map.addLayer(vectorLayer);

            this.addMarkers();
        }

        setupReferenceOverlay(){

            const config = di.get('config');

            const vectorSource = new SrcVector({
                url: config.mapShapefileUrl,
                format: new GeoJSON({
                    dataProjection: 'EPSG:28992'
                })
            });

            const vectorLayer = new LayerVector({
                id: 'reference',
                source: vectorSource,
                style: getReferenceStyle
            });

            this.map.addLayer(vectorLayer);
        }

        addMarkers() {

            var newCoordinates = [];

            for(const marker of this.markers) {

                newCoordinates.push(marker.coordinates);

                const iconFeature = new Feature({
                    geometry: new Point(marker.coordinates)
                });

                iconFeature.setId(marker.id);

                this.markersSource.addFeature(iconFeature);
            }

            this.allCoordinates = [...this.allCoordinates, ...newCoordinates];
        }

        clearMarkers(){

            this.markersSource.clear();
            this.allCoordinates = [];
        }

        setZoom(zoom) {

            return new Promise((resolve) => {

                if(zoom === this.map.getView().getZoom()) {
                    return resolve();
                }

                this.map.once('moveend', resolve);

                this.map.getView().setZoom(zoom);
            })
        }

        getZoom(){

            return this.map.getView().getZoom();
        }

        addSelectInteraction(){

            this.selectInteraction = new DrawInteraction({
                source: this.drawSource,
                type: 'Polygon'
            });

            let selectedMarkers = [];
            this.selectInteraction.on('drawend', event => {

                const polygon = event.feature.getGeometry();

                for(const marker of this.markers) {

                    const intersected = polygon.intersectsCoordinate(marker.coordinates);
                    if(intersected) {
                        selectedMarkers.push(marker.id);
                    }
                }

                this.$emit('selection', selectedMarkers);
            });

            this.map.addInteraction(this.selectInteraction);
        }

        removeSelectInteraction(){

            if(!this.selectInteraction){
                return;
            }

            this.map.removeInteraction(this.selectInteraction);
            this.drawSource.clear();

            this.selectInteraction = null;
        }

        tileLoadingStarted() {

            this.debouncedTileLoadingCompleted.cancel();

            this.tileLoading++;
        }

        tileLoadingEnded() {

            this.tileLoaded++;

            const isDoneLoading = (this.tileLoading / this.tileLoaded) === 1;

            if(isDoneLoading) {

                this.tileLoading = 0;
                this.tileLoaded = 0;
                this.debouncedTileLoadingCompleted();
            }
        }

        tileLoadingCompleted(){

            this.$emit('tiles-loaded');
        }

        onZoomClick(direction) {

            const view = this.map.getView();

            view.animate({
                zoom: view.getZoom() + direction,
                duration: 200
            });
        }
    }

</script>
